// Searchable member dropdown. Two modes:
//   - <MemberPicker value={ownerId} onChange={(id) => ...} /> — single-select,
//     renders a button + popover. Used in forms, inline edit, bulk-assign.
//   - <MemberPickerPanel value onChange /> — bare panel content for embedding
//     inside an already-anchored popover (filter chip uses this).
//
// The selectable list (MemberPickerPanel) is scoped to the currently-selected
// project via useProjectScopedMembers() — you can only assign/filter by owners
// who belong to the active project. The button label + read-only badge still
// resolve names against the full org roster (useTeamMembers) so an already-set
// owner never renders blank even if they're not on the project.

import React, { useMemo, useState } from "react";
import { Search, User, ChevronDown, X, Check } from "lucide-react";
import { useTeamMembers, useProjectScopedMembers, formatMember, isIdentifiableMember } from "../hooks/useTeamMembers";
import { resolveUserAvatar } from "../../../utils/userAvatar";

function Avatar({ user, size = 22 }) {
  const initials = (user
    ? [user.firstName, user.lastName].filter(Boolean).map((s) => s[0]).join("").slice(0, 2).toUpperCase()
    : "?") || "?";
  const src = resolveUserAvatar(user);
  if (src) {
    return <img src={src} alt="" style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover" }} />;
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: "var(--crm-primary-soft)", color: "var(--crm-primary)",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontSize: Math.round(size * 0.4), fontWeight: 600,
    }}>{initials}</div>
  );
}

// Note: "Unassigned" option is OFF by default. Owners are always required —
// leads, calls, meetings etc. all need an accountable user. Callers that
// genuinely need a clearable picker (e.g. filter chips on the leads list)
// can opt back in with `includeUnassigned`.
// `values` — optional string[] for multi-select callers (e.g. OwnerChip filter).
// When provided, active state is checked against the set instead of single `value`.
// `compact` — drops the email line and shrinks the avatar. Used where the panel
// sits alongside other single-line option rows (the leads filter) and the
// two-line rows made that list look like a different control.
export function MemberPickerPanel({ value, values, onChange, allowClear = true, includeUnassigned = false, compact = false }) {
  const { members, isLoading } = useProjectScopedMembers();
  const [q, setQ] = useState("");
  const selectedSet = useMemo(() => new Set(Array.isArray(values) ? values.map(String) : []), [values]);
  const filtered = useMemo(() => {
    const base = members.filter(isIdentifiableMember);
    if (!q.trim()) return base;
    const needle = q.toLowerCase();
    return base.filter((u) =>
      [u.firstName, u.lastName, u.email, u.stubEmail, u.position].filter(Boolean).join(" ").toLowerCase().includes(needle),
    );
  }, [members, q]);

  return (
    <div style={{ minWidth: 0, width: "100%" }}>
      <div style={{ position: "relative", marginBottom: 6 }}>
        <Search size={12} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "var(--color-text-secondary)" }} />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search members"
          style={{
            // Without border-box the padding and border push this past 100% of
            // the popover and the field spills out of its own panel.
            boxSizing: "border-box",
            width: "100%", height: compact ? 28 : 30, padding: "0 8px 0 26px",
            borderRadius: 6, border: "1px solid var(--color-border-subtle)",
            background: "var(--color-bg-base)", color: "var(--color-text-primary)",
            fontSize: compact ? 12 : 13, outline: "none",
          }}
        />
      </div>
      <div style={{ maxHeight: "min(240px, 40vh)", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        {isLoading && (
          <div style={{ padding: "8px 10px", fontSize: 12, color: "var(--color-text-secondary)" }}>Loading…</div>
        )}
        {!isLoading && includeUnassigned && allowClear && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="tg-filter-opt"
            style={rowStyle(value == null)}
          >
            <span style={{ width: 22, height: 22, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--color-text-secondary)" }}>
              <User size={14} />
            </span>
            <span style={{ flex: 1, textAlign: "left", color: "var(--color-text-secondary)" }}>Unassigned</span>
            {value == null && <Check size={12} style={{ color: "var(--crm-primary)" }} />}
          </button>
        )}
        {!isLoading && filtered.length === 0 && (
          <div style={{ padding: "8px 10px", fontSize: 12, color: "var(--color-text-secondary)" }}>No members match</div>
        )}
        {filtered.map((u) => {
          const id = String(u._id);
          const active = values !== undefined ? selectedSet.has(id) : String(value || "") === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onChange(id)}
              className="tg-filter-opt"
              // Compact rows hide the email, so keep it reachable on hover.
              title={compact && u.email ? `${formatMember(u)} · ${u.email}` : undefined}
              style={rowStyle(active)}
            >
              <span style={{
                width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                border: `1.5px solid ${active ? "var(--crm-primary)" : "#94a3b8"}`,
                background: active ? "var(--crm-primary)" : "transparent",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}>
                {active && <Check size={9} style={{ color: "#fff" }} />}
              </span>
              <Avatar user={u} size={compact ? 18 : 22} />
              <span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                <div style={{ fontSize: 13, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {formatMember(u)}
                </div>
                {!compact && u.email && (
                  <div style={{ fontSize: 11, color: "var(--color-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {u.email}
                  </div>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function rowStyle(active) {
  return {
    display: "flex", alignItems: "center", gap: 8, width: "100%",
    padding: "6px 8px", borderRadius: 6,
    border: "none",
    // Left unset when inactive so the hover rule applies; see .tg-filter-opt.
    ...(active ? { background: "var(--crm-primary-soft)" } : null),
    cursor: "pointer", textAlign: "left",
  };
}

export default function MemberPicker({ value, onChange, placeholder = "Select owner", disabled, width = 220 }) {
  const [open, setOpen] = useState(false);
  const { byId, isLoading } = useTeamMembers();
  const selected = value ? byId.get(String(value)) : null;
  const fullWidth = width === "100%";

  return (
    <div style={{ position: "relative", display: fullWidth ? "block" : "inline-block", width: fullWidth ? "100%" : undefined }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          width, height: 34, padding: "0 10px",
          borderRadius: 8, border: "1px solid var(--color-border-subtle)",
          background: "var(--color-bg-surface)", color: "var(--color-text-primary)",
          fontSize: 13, cursor: disabled ? "not-allowed" : "pointer",
          textAlign: "left",
        }}
      >
        {selected ? <Avatar user={selected} size={20} /> : <User size={14} style={{ color: "var(--color-text-secondary)" }} />}
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: selected ? "inherit" : "var(--color-text-secondary)" }}>
          {isLoading ? "Loading…" : selected ? formatMember(selected) : placeholder}
        </span>
        <ChevronDown size={12} style={{ opacity: 0.6 }} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 800 }} />
          <div style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 801,
            background: "var(--color-bg-surface)",
            border: "1px solid var(--color-border-subtle)",
            borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
            padding: 8, minWidth: 260,
          }}>
            <MemberPickerPanel value={value} onChange={(id) => { onChange(id); setOpen(false); }} />
          </div>
        </>
      )}
    </div>
  );
}

// Small read-only display badge — used in lead rows / drawer where we want
// to show an owner without a clickable picker.
export function MemberBadge({ userId, fallback = "Unassigned" }) {
  const { byId } = useTeamMembers();
  const u = userId ? byId.get(String(userId)) : null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--color-text-primary)" }}>
      {u ? <Avatar user={u} size={18} /> : <span style={{ width: 18, height: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--color-text-secondary)" }}><User size={12} /></span>}
      <span style={{ color: u ? "inherit" : "var(--color-text-secondary)" }}>{u ? formatMember(u) : fallback}</span>
    </span>
  );
}
